import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MapPin, ArrowUpRight } from "lucide-react";
import { Seo } from "../seo/Seo";
import { getService, STATE_SERVICES } from "../seo/services";
import { getState, STATES, SITE } from "../seo/states";
import { PageShell, CTASection } from "../components/PageShell";

export default function StateServicePage() {
  const { serviceSlug, stateSlug } = useParams();
  const svc = getService(serviceSlug);
  const st = getState(stateSlug);
  if (!svc || !st || !STATE_SERVICES.includes(serviceSlug)) return <Navigate to="/" replace />;

  const isLLC = svc.slug === "llc-formation";
  const url = `${SITE}/services/${svc.slug}/${st.slug}`;
  const title = isLLC
    ? `${st.name} LLC Formation | Start Your ${st.name} LLC | Alexa Financial`
    : `Professional Website Design in ${st.name} | Custom Sites | Alexa Financial`;
  const description = isLLC
    ? `Form your ${st.name} LLC the right way. State filing with ${st.agency}, EIN, operating agreement & registered agent. Serving ${st.cities.slice(0, 3).join(", ")} and all of ${st.name}.`
    : `Custom, conversion-focused website design for ${st.name} businesses. Mobile responsive, fast, SEO-ready. Serving ${st.cities.slice(0, 3).join(", ")} and all of ${st.name}.`;

  const h1 = isLLC ? `${st.name} LLC Formation Services` : `Professional Website Design in ${st.name}`;
  const intro = isLLC
    ? `Starting a business in ${st.name}? We handle your entire LLC formation — filing with the ${st.agency}, securing your EIN, preparing your operating agreement and providing registered agent service — so entrepreneurs in ${st.cities.slice(0, 3).join(", ")} and across ${st.name} can launch with confidence.`
    : `Businesses in ${st.name} deserve a website that builds trust and wins clients. We design custom, mobile-responsive, SEO-ready sites for companies in ${st.cities.slice(0, 3).join(", ")} and throughout ${st.name} — no templates, no copy-and-paste.`;

  const benefits = isLLC
    ? [
        `Filing handled directly with the ${st.agency}`,
        "EIN (Federal Tax ID) included",
        "Custom operating agreement",
        "Registered agent service available",
        `Guidance on ${st.name} compliance requirements`,
        "Ready for business credit & funding",
      ]
    : [
        `Custom design for ${st.name} businesses`,
        "Mobile responsive & fast loading",
        "Local SEO for your service area",
        "Contact forms & WhatsApp integration",
        "Google Maps & social integration",
        "Built to convert local visitors",
      ];

  const faqs = isLLC
    ? [
        { q: `How do I form an LLC in ${st.name}?`, a: `We prepare and file your Articles of Organization with the ${st.agency}, secure your EIN, and deliver your operating agreement — handling the entire ${st.name} process for you.` },
        { q: `How much does a ${st.name} LLC cost?`, a: `${st.name} charges a state filing fee, which we include in our packages. ${st.note}` },
        { q: `How long does ${st.name} LLC formation take?`, a: `Most ${st.name} filings are processed within a few business days, with expedited options available in many cases.` },
        { q: `Do you serve my city in ${st.name}?`, a: `Yes — we serve entrepreneurs in ${st.cities.join(", ")} and every community across ${st.name}.` },
      ]
    : [
        { q: `Do you design websites for ${st.name} businesses?`, a: `Yes. We design custom websites for businesses in ${st.cities.join(", ")} and throughout ${st.name}.` },
        { q: `Will my ${st.name} website rank on Google?`, a: `Every site is SEO-ready with local SEO for ${st.name}, and advanced SEO is available to help you rank for local searches.` },
        { q: `How much does a website cost in ${st.name}?`, a: `Landing pages start at $299.99, business websites at $499.99, and premium e-commerce sites start at $799.99 — each custom-built for your ${st.name} business.` },
        { q: `Can you help with more than a website?`, a: `Absolutely — we also handle LLC formation, EIN, business credit and funding for ${st.name} entrepreneurs.` },
      ];

  const localBiz = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Alexa Financial Multiservices",
    description,
    url,
    telephone: "+1-240-817-6538",
    email: "Alexaguzman407@gmail.com",
    areaServed: { "@type": "State", name: st.name },
    address: { "@type": "PostalAddress", addressRegion: st.abbr, addressCountry: "US" },
  };
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `${svc.name} in ${st.name}`,
    areaServed: { "@type": "State", name: st.name },
    provider: { "@type": "ProfessionalService", name: "Alexa Financial Multiservices", url: SITE },
    url,
    description,
  };
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  const otherStates = STATES.filter((s) => s.slug !== st.slug).slice(0, 8);

  return (
    <PageShell
      eyebrow={`${svc.name} · ${st.name}`}
      title={h1}
      subtitle={intro}
      breadcrumb={[{ label: "Home", to: "/" }, { label: svc.name, to: `/services/${svc.slug}` }, { label: st.name }]}
    >
      <Seo title={title} description={description} keywords={`${svc.name} ${st.name}, ${st.name} ${isLLC ? "LLC" : "website design"}, ${st.cities[0]} ${svc.name}`} canonical={url} jsonLd={[localBiz, serviceLd, faqLd]} />

      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Why {st.name} entrepreneurs choose us</h2>
          <p className="text-[#a3a3a3] max-w-2xl mb-10 flex items-center gap-2"><MapPin size={16} className="text-[#d4af37]" /> Serving {st.cities.join(", ")} and all of {st.name}.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((bft, i) => (
              <div key={i} className="glass rounded-2xl p-6 flex items-start gap-4">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-[#d4af37]/15 flex items-center justify-center flex-shrink-0"><Check size={14} className="text-[#d4af37]" /></span>
                <span className="text-[#d4d4d4]">{bft}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#080808]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-12">Our {svc.name} process in {st.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {svc.process.map((p, i) => (
              <div key={i} className="glass rounded-2xl p-7">
                <div className="font-display text-4xl text-[#d4af37]/40">0{i + 1}</div>
                <h3 className="mt-4 font-display text-xl text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-[#a3a3a3] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-10">{svc.name} in {st.name} — FAQs</h2>
          <div className="space-y-5">
            {faqs.map((f, i) => (
              <div key={i} className="border-b border-white/10 pb-5">
                <h3 className="font-display text-lg md:text-xl text-white">{f.q}</h3>
                <p className="mt-2 text-[#a3a3a3] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-[#080808] border-t border-white/10">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <h2 className="font-display text-2xl text-white mb-6">Related services</h2>
          <div className="flex flex-wrap gap-3 mb-10">
            {svc.related.map((r) => {
              const rs = getService(r);
              return rs ? (
                <Link key={r} to={`/services/${rs.slug}`} className="inline-flex items-center gap-2 glass rounded-full px-5 py-2.5 text-sm text-[#d4d4d4] hover:text-white hover:border-[#d4af37]/40 transition-colors">
                  {rs.name} <ArrowUpRight size={14} className="text-[#d4af37]" />
                </Link>
              ) : null;
            })}
          </div>
          <h2 className="font-display text-2xl text-white mb-6">{svc.name} in other states</h2>
          <div className="flex flex-wrap gap-2.5">
            {otherStates.map((s) => (
              <Link key={s.slug} to={`/services/${svc.slug}/${s.slug}`} className="text-sm text-[#a3a3a3] hover:text-[#d4af37] transition-colors border border-white/10 rounded-full px-4 py-2">
                {svc.name} in {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection text={`Start your ${st.name} ${isLLC ? "LLC" : "website"} today.`} />
    </PageShell>
  );
}
